import { Suspense, lazy, useState, useEffect } from 'react';
import './App.css';
import { LicenseInfo } from '@mui/x-license';
import { AutosaveProvider } from './cpts/Form/Autosave/AutosaveContext';
import cuteSadGnomeSrc from './assets/art/cute-sad-gnome.webp'; // KEEEEEP
import { getLocalStorageMB } from './libTs/depless';

// Lazy-load a component based on the tenant ID
// Define the TenantAppName type and component paths

export type TenantAppName =
  | 'BELONGING'
  | 'DEMO'
  | 'EHC'
  | 'EHC2'
  | 'KS'
  | 'KSTRAIN'
  | 'MNHOUSTON'
  | 'MO'
  | 'NCSTATE'
  | 'SCOTT2'
  | 'SG'
  | 'TEST';

export type TenantAppNameLowercase = Lowercase<TenantAppName>;

// Map of tenant apps that actually exist
const EXISTING_TENANTS = {
  BELONGING: true,
  DEMO: true,
  EHC: true,
  EHC2: true,
  KS: true,
  KSTRAIN: true,
  MNHOUSTON: true,
  MO: true,
  NCSTATE: true,
  SCOTT2: true,
  SG: true,
  TEST: true,
} as const;


// Component that dynamically loads the appropriate tenant app
function TenantApp() {
  const [tenantDir, setTenantDir] = useState<TenantAppName | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // First try to get from environment variable
    const envTenantDir = import.meta.env.VITE_FRONTEND_TENANT_DIR;
    
    if (envTenantDir) {
      console.log('Using tenant directory from environment:', envTenantDir);
      setTenantDir(envTenantDir as TenantAppName);
      return;
    }

    // If environment variable is not set, show error and fetch from backend
    console.error('VITE_FRONTEND_TENANT_DIR not found in environment, falling back to HSPPConfig from backend');
    
    // Fetch from backend HSPPConfig using lightweight endpoint
    fetch('/api/frontend-tenant-dir')
      .then(response => response.json())
      .then(data => {
        const backendTenantDir = data.frontendAppDirName;
        if (backendTenantDir) {
          console.log('Using tenant directory from HSPPConfig backend:', backendTenantDir);
          setTenantDir(backendTenantDir as TenantAppName);
        } else {
          console.error('No frontendAppDirName found in response');
          setError('No frontendAppDirName found in response');
        }
      })
      .catch(error => {
        console.error('Failed to fetch frontend tenant directory from backend:', error);
        setError('Failed to fetch frontend tenant directory from backend: ' + error.message);
      });
  }, []);

  if (!tenantDir) {
    if (error) {
      return (
        <div style={{ background: 'red', color: 'white', padding: '20px', margin: '20px' }}>
          <h2>Failed to load tenant configuration</h2>
          <p>Error: {error}</p>
          <p>Please ensure either:</p>
          <ul>
            <li>VITE_FRONTEND_TENANT_DIR is set in environment variables, or</li>
            <li>HSPPConfig.frontendAppDirName is properly configured in the backend</li>
          </ul>
        </div>
      );
    }
    return <div>Loading tenant configuration...</div>;
  }

  // Set global variable for backwards compatibility
  window.FRONTEND_TENANT_DIR = tenantDir;

  // Dynamically import the appropriate tenant app
  const getTenantComponent = (tenant: TenantAppName) => {
    if (!EXISTING_TENANTS[tenant]) {
      return lazy(() => Promise.resolve({ default: () => <div>Tenant {tenant} not configured</div> }));
    }
    
    switch (tenant) {
      case 'BELONGING':
        return lazy(() => import('./_tenant_apps/BELONGING/App'));
      case 'DEMO':
        return lazy(() => import('./_tenant_apps/DEMO/App'));
      case 'EHC':
        return lazy(() => import('./_tenant_apps/EHC/App'));
      case 'EHC2':
        return lazy(() => import('./_tenant_apps/EHC2/App'));
      case 'KS':
        return lazy(() => import('./_tenant_apps/KS/App'));
      case 'KSTRAIN':
        return lazy(() => import('./_tenant_apps/KSTRAIN/App'));
      case 'MNHOUSTON':
        return lazy(() => import('./_tenant_apps/MNHOUSTON/App'));
      case 'MO':
        return lazy(() => import('./_tenant_apps/MO/App'));
      case 'NCSTATE':
        return lazy(() => import('./_tenant_apps/NCSTATE/App'));
      case 'SCOTT2':
        return lazy(() => import('./_tenant_apps/SCOTT2/App'));
      case 'SG':
        return lazy(() => import('./_tenant_apps/SG/App'));
      case 'TEST':
        return lazy(() => import('./_tenant_apps/TEST/App'));
      default:
        return lazy(() => Promise.resolve({ default: () => <div>Tenant {tenant} not configured</div> }));
    }
  };

  const TenantRootComponent = getTenantComponent(tenantDir);

  return (
    <>
      {error && (
        <div style={{ background: 'yellow', padding: '10px', margin: '10px' }}>
          Warning: {error}
        </div>
      )}
      <Suspense fallback={<div>Loading tenant app: {tenantDir}...</div>}>
        <TenantRootComponent />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <AutosaveProvider>
      <TenantApp />
      {/* prettier-ignore */}
      <img src={cuteSadGnomeSrc} alt="Cute sad gnome" width={'1px'} height={'1px'} style={{opacity: 0, display: 'none'}} />
    </AutosaveProvider>
  );
}

LicenseInfo.setLicenseKey(
  '6b0286321fcdebb1f0592fa8339ab1e2Tz0xMDM0MzEsRT0xNzY0ODE5NzE2MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y'
);

export default App;

declare global {
  interface Window {
    tools: {
      getLocalStorageMB: () => number;
    };
    FRONTEND_TENANT_DIR?: string;
  }
}
window.tools = { getLocalStorageMB };
